// @flow

import React, { Component } from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import { addDays, format, subDays } from 'date-fns';
import { injectIntl } from 'react-intl';
import type { InjectIntlProvidedProps } from 'react-intl';

import DayPickerWrapper from './';
import MONTHS, {
  WEEKDAYS_SHORT,
  FIRST_DAY_OF_WEEK,
  WEEKDAYS_LONG,
} from './calendarLocales';

type Props = InjectIntlProvidedProps & {
  fromDate: Date,
  toDate: Date,
  minDate: Date,
  maxDate: Date,
  maxPeriod?: number,
  isMonthsPresets?: Boolean,
  datePresets: Array<{
    periodDays: number,
    fromDate: Date,
    toDate: Date,
  }>,
  subscriptionIsActive: boolean,
  premiumPlan?: boolean,
  onApplyPeriod: (any, any) => any,
};

type State = {
  from: Date | null,
  to: Date | null,
  enteredFrom: Date | null,
  enteredTo: Date | null,
  selectorFrom: Date | null,
  selectorTo: Date | null,
  minDate: Date,
  maxDate: Date,
};

class DayPickerContainer extends Component<Props, State> {
  state = {
    from: this.props.fromDate,
    to: this.props.toDate,
    enteredFrom: this.props.fromDate, // Keep track of the first day for mouseEnter.
    enteredTo: this.props.toDate, // Keep track of the last day for mouseEnter.
    selectorFrom: this.props.fromDate,
    selectorTo: this.props.toDate,
    minDate: this.props.minDate,
    maxDate: this.props.maxDate,
  };

  isSelectingFirstDay(from: Date | null, to: Date | null) {
    const isRangeSelected = from && to;
    return !from || isRangeSelected;
  }

  handleDayClick = (day: Date) => {
    const { from, to, minDate, maxDate } = this.state;
    const maxPeriod = this.props.maxPeriod;

    if (from && to && day >= from && day <= to) {
      this.handleResetClick();
      return;
    }

    if (
      this.isSelectingFirstDay(from, to) &&
      DateUtils.isDayInRange(day, { from: minDate, to: maxDate })
    ) {
      this.setState({
        from: day,
        enteredFrom: day,
        to: null,
        enteredTo: null,
      });
    } else if (
      DateUtils.isDayBetween(day, from, maxDate) ||
      DateUtils.isSameDay(day, from) ||
      DateUtils.isSameDay(day, maxDate)
    ) {
      if (maxPeriod) {
        const maxPeriodSelectedDay = addDays(from, maxPeriod);
        if (!DateUtils.isDayBetween(day, from, maxPeriodSelectedDay)) {
          this.setState({
            to: maxPeriodSelectedDay,
            enteredTo: maxPeriodSelectedDay,
          });
        } else {
          this.setState({
            to: day,
            enteredTo: day,
          });
        }
      } else {
        this.setState({
          to: day,
          enteredTo: day,
        });
      }
    } else if (
      DateUtils.isDayBetween(day, minDate, from) ||
      DateUtils.isSameDay(day, minDate)
    ) {
      if (maxPeriod) {
        const minPeriodSelectedDay = subDays(from, maxPeriod);
        if (!DateUtils.isDayBetween(day, minPeriodSelectedDay, from)) {
          this.setState({
            to: from,
            enteredTo: from,
            from: minPeriodSelectedDay,
            enteredFrom: minPeriodSelectedDay,
          });
        } else {
          this.setState({
            to: from,
            enteredTo: from,
            from: day,
          });
        }
      } else {
        this.setState({
          to: from,
          enteredTo: from,
          from: day,
        });
      }
    }
  };

  handleDayMouseEnter = (day: Date) => {
    const { from, to, minDate, maxDate } = this.state;
    if (!this.isSelectingFirstDay(from, to)) {
      if (
        DateUtils.isDayBetween(day, from, maxDate) ||
        DateUtils.isSameDay(day, from) ||
        DateUtils.isSameDay(day, maxDate)
      ) {
        this.setState({
          enteredFrom: from,
          enteredTo: day,
        });
      } else if (
        DateUtils.isDayBetween(day, minDate, from) ||
        DateUtils.isSameDay(day, minDate)
      ) {
        this.setState({
          enteredTo: from,
          enteredFrom: day,
        });
      }
    }
  };

  handleResetClick = () => {
    this.setState({
      from: null,
      to: null,
      enteredFrom: null,
      enteredTo: null,
    });
  };

  handleSetPeriod = (from: Date, to: Date) => {
    this.setState({
      from: from,
      to: to,
      enteredFrom: to,
      enteredTo: from,
      selectorFrom: from,
      selectorTo: to,
    });
    const formattedISOFrom = format(from, 'YYYY-MM-DD');
    const formattedISOTo = format(to, 'YYYY-MM-DD');

    this.props.onApplyPeriod(formattedISOFrom, formattedISOTo);
  };

  handleApplyPeriod = (from: Date, to: Date) => {
    this.setState({
      selectorFrom: from,
      selectorTo: to,
    });

    this.props.onApplyPeriod(from, to);
  };

  render() {
    const {
      from,
      to,
      enteredFrom,
      enteredTo,
      selectorFrom,
      selectorTo,
    } = this.state;
    const modifiers = { start: enteredFrom, end: enteredTo };
    const disabledDays = {
      before: this.props.minDate,
      after: this.props.maxDate,
    };
    const selectedDays = [from, { from: enteredFrom, to: enteredTo }];
    return (
      <DayPickerWrapper
        selectorFrom={selectorFrom}
        selectorTo={selectorTo}
        from={from}
        to={to}
        onResetClick={this.handleResetClick}
        onChangePreset={this.handleSetPeriod}
        onApplyPeriod={this.handleApplyPeriod}
        isMonthsPresets={this.props.isMonthsPresets}
        datePresets={this.props.datePresets}
        subscriptionIsActive={this.props.subscriptionIsActive}
        premiumPlan={this.props.premiumPlan}
      >
        <DayPicker
          className="RangeDayPicker"
          numberOfMonths={2}
          fromMonth={this.props.minDate}
          toMonth={this.props.maxDate}
          initialMonth={this.props.maxDate}
          from={from}
          to={to}
          modifiers={modifiers}
          selectedDays={selectedDays}
          disabledDays={disabledDays}
          onDayClick={this.handleDayClick}
          onDayMouseEnter={this.handleDayMouseEnter}
          locale={this.props.intl.locale}
          months={MONTHS[this.props.intl.locale]}
          weekdaysShort={WEEKDAYS_SHORT[this.props.intl.locale]}
          firstDayOfWeek={FIRST_DAY_OF_WEEK[this.props.intl.locale]}
          weekdaysLong={WEEKDAYS_LONG[this.props.intl.locale]}
        />
      </DayPickerWrapper>
    );
  }
}

export default injectIntl(DayPickerContainer);



// WEBPACK FOOTER //
// ./src/buisness/CommonFilters/filters/DayPicker/container.js